import { useEffect } from 'react'

interface SocialMeta {
  ogImage?: string
  ogType?: string
  ogUrl?: string
  twitterCard?: 'summary' | 'summary_large_image'
}

const DEFAULT_OG_IMAGE =
  'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80'

function setMetaTag(
  property: string,
  content: string,
  attrName: 'property' | 'name' = 'property',
) {
  let tag = document.querySelector(
    `meta[${attrName}="${property}"]`,
  ) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attrName, property)
    document.head.appendChild(tag)
  }
  tag.content = content
}

function removeMetaTag(
  property: string,
  attrName: 'property' | 'name' = 'property',
) {
  const tag = document.querySelector(`meta[${attrName}="${property}"]`)
  if (tag) tag.remove()
}

export function usePageMeta(
  title: string,
  description: string,
  social?: SocialMeta,
) {
  useEffect(() => {
    const ogImage = social?.ogImage || DEFAULT_OG_IMAGE
    const ogType = social?.ogType || 'website'
    const twitterCard = social?.twitterCard || 'summary_large_image'
    const ogUrl = social?.ogUrl || window.location.href

    // Set document title
    document.title = title

    // Set or create meta description
    let metaDesc = document.querySelector(
      'meta[name="description"]',
    ) as HTMLMetaElement | null
    if (!metaDesc) {
      metaDesc = document.createElement('meta')
      metaDesc.name = 'description'
      document.head.appendChild(metaDesc)
    }
    metaDesc.content = description

    // Open Graph tags
    setMetaTag('og:title', title)
    setMetaTag('og:description', description)
    setMetaTag('og:image', ogImage)
    setMetaTag('og:type', ogType)
    setMetaTag('og:url', ogUrl)
    setMetaTag('og:site_name', 'OnlineCollegeGuide')

    // Twitter Card tags
    setMetaTag('twitter:card', twitterCard, 'name')
    setMetaTag('twitter:title', title, 'name')
    setMetaTag('twitter:description', description, 'name')
    setMetaTag('twitter:image', ogImage, 'name')

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = 'OnlineCollegeGuide | Find Your Path Forward'
      if (metaDesc) {
        metaDesc.content =
          'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget.'
      }
      // Remove OG and Twitter tags on unmount so stale data doesn't persist
      removeMetaTag('og:title')
      removeMetaTag('og:description')
      removeMetaTag('og:image')
      removeMetaTag('og:type')
      removeMetaTag('og:url')
      removeMetaTag('og:site_name')
      removeMetaTag('twitter:card', 'name')
      removeMetaTag('twitter:title', 'name')
      removeMetaTag('twitter:description', 'name')
      removeMetaTag('twitter:image', 'name')
    }
  }, [
    title,
    description,
    social?.ogImage,
    social?.ogType,
    social?.ogUrl,
    social?.twitterCard,
  ])
}
